import React, { useState } from "react";
import { Table } from "react-bootstrap";
import { fmtAmount } from "./format";

export interface TxColumn {
  /** Column header label */
  header: React.ReactNode;
  /** Cell renderer for a given row */
  cell: (row: any) => React.ReactNode;
  align?: "left" | "right" | "center";
  nowrap?: boolean;
  /** Fixed column width (px) */
  width?: number;
}

interface ExpandableTxTableProps {
  rows: any[];
  columns: TxColumn[];
  /** Extra class for the parent row (e.g. reversed-row tint) */
  rowClassName?: (row: any) => string | undefined;
  rowKey?: (row: any, index: number) => React.Key;
  /**
   * Full override for the expanded detail panel. When omitted, the default
   * renderer prints each `row.details[]` line (installment · head · amount)
   * followed by a Created By / Created Date / Remarks footer — mirroring the
   * Java enrollment-view expand rows.
   */
  renderDetails?: (row: any) => React.ReactNode;
  /** Appended below the default detail footer (e.g. reversal / discount notes) */
  detailFooterExtra?: (row: any) => React.ReactNode;
  /** Rows for which the expand arrow should be hidden (no details to show) */
  hasDetails?: (row: any) => boolean;
}

/** Default detail panel: line-items + audit footer (Java parity) */
const DefaultDetails: React.FC<{
  row: any;
  detailFooterExtra?: (row: any) => React.ReactNode;
}> = ({ row, detailFooterExtra }) => {
  const details: any[] = row.details || [];
  return (
    <>
      {details.map((d: any, i: number) => (
        <div className="ev-detail-line" key={i}>
          {d.installmentName && (
            <span className="ev-detail-inst">{d.installmentName}</span>
          )}
          <span className="ev-detail-head">{d.headName}</span>
          <span className="ev-detail-amt">{fmtAmount(d.amount)}</span>
        </div>
      ))}
      <div className="ev-detail-meta">
        {row.createdByName && (
          <span>
            Created By: <b>{row.createdByName}</b>
            {row.createdDate ? ` · Created Date: ${row.createdDate}` : ""}
          </span>
        )}
        {row.remarks && (
          <span>
            {row.createdByName ? " · " : ""}Remarks: <b>{row.remarks}</b>
          </span>
        )}
        {detailFooterExtra && detailFooterExtra(row)}
      </div>
    </>
  );
};

const ExpandableTxTable: React.FC<ExpandableTxTableProps> = ({
  rows,
  columns,
  rowClassName,
  rowKey,
  renderDetails,
  detailFooterExtra,
  hasDetails,
}) => {
  const [openKeys, setOpenKeys] = useState<Set<React.Key>>(new Set());

  const toggle = (k: React.Key) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const totalCols = columns.length + 1; // +1 for the expand column

  return (
    <div style={{ overflowX: "auto" }}>
      <Table bordered hover size="sm" style={{ fontSize: 12, marginBottom: 0 }}>
        <thead>
          <tr>
            <th className="ev-expand-cell" />
            {columns.map((c, i) => (
              <th
                key={i}
                style={{
                  textAlign: c.align || "left",
                  width: c.width,
                  whiteSpace: c.nowrap ? "nowrap" : undefined,
                }}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row: any, idx: number) => {
            const key = rowKey ? rowKey(row, idx) : row.id ?? idx;
            const expandable = hasDetails
              ? hasDetails(row)
              : (row.details && row.details.length > 0) || !!renderDetails;
            const open = openKeys.has(key);
            const extraCls = rowClassName ? rowClassName(row) : undefined;
            return (
              <React.Fragment key={key}>
                <tr
                  className={`ev-parent-row${extraCls ? " " + extraCls : ""}`}
                  onClick={() => expandable && toggle(key)}
                >
                  <td className="ev-expand-cell">
                    {expandable && (
                      <button
                        type="button"
                        className="ev-expand-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggle(key);
                        }}
                        title={open ? "Collapse" : "Expand"}
                      >
                        <i
                          className={`fa fa-chevron-${open ? "up" : "down"}`}
                        />
                      </button>
                    )}
                  </td>
                  {columns.map((c, i) => (
                    <td
                      key={i}
                      style={{
                        textAlign: c.align || "left",
                        whiteSpace: c.nowrap ? "nowrap" : undefined,
                      }}
                    >
                      {c.cell(row)}
                    </td>
                  ))}
                </tr>
                {expandable && open && (
                  <tr className="ev-detail-row">
                    <td colSpan={totalCols}>
                      {renderDetails ? (
                        renderDetails(row)
                      ) : (
                        <DefaultDetails
                          row={row}
                          detailFooterExtra={detailFooterExtra}
                        />
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default ExpandableTxTable;
